/**
 * Founder 2026-10-20 — Security › Trusted.
 *
 * Moves the trusted-address table out of Settings → Blocking into its
 * own tab, and lets owner / admin add an entry by hand (IP or range +
 * reason). A "Trust my current address" button pre-fills with the
 * caller's IP as the API sees it (GET /v1/sites/:site_id/my-ip —
 * owner / admin / reviewer only).
 *
 * Validation rules mirror the API:
 *   - up to 500 entries per site
 *   - IPv4 /32 or IPv6 /128 (bare address) OR a range no wider than
 *     /24 for IPv4, /48 for IPv6
 *   - reason required, up to 256 characters
 *
 * Role gate: owner / admin can add / remove; reviewer / viewer get
 * the read-only table (same `canMutate` rule as the API).
 *
 * Admin reachability: adding your office / jump host here means
 * attack-paths and the block list can never refuse the WordPress
 * admin, cPanel or similar for that address. Attack paths still apply
 * to anyone NOT trusted — a trusted address is the only safe way to
 * keep admin reachable from your network.
 */

import { useCallback, useEffect, useState } from 'react';

import type {
  AllowlistEntryWire,
} from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import {
  addAllowlistEntry,
  deleteAllowlistEntry,
  getMyIp,
  listAllowlist,
} from '../../api/threats.js';
import type { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/index.js';
import { TrustedAddressTable } from '../settings/AddressTables.js';
import { useC } from '../../lib/theme.js';
import type { Site } from '@sill/types';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/* --------------------------- client validation --------------------------- */

/** IPv4 dotted quad, strict octets. */
function isIpv4(raw: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(raw);
  if (m === null) return false;
  for (let i = 1; i <= 4; i += 1) {
    const n = Number(m[i]);
    if (!Number.isInteger(n) || n < 0 || n > 255) return false;
  }
  return true;
}

/** IPv6, loose enough to accept the compressed form; node:net.isIP(6) semantics. */
function isIpv6(raw: string): boolean {
  if (raw.length === 0 || raw.length > 45) return false;
  if (!/^[0-9a-fA-F:]+$/.test(raw)) return false;
  // Reject obvious junk; the API does the strict check.
  if (raw.includes(':::')) return false;
  const parts = raw.split('::');
  if (parts.length > 2) return false;
  const countGroups = (s: string): number => (s.length === 0 ? 0 : s.split(':').length);
  const left = countGroups(parts[0] ?? '');
  const right = parts.length === 2 ? countGroups(parts[1] ?? '') : 0;
  if (parts.length === 1) return left === 8;
  return left + right <= 7;
}

export type ValidationError =
  | { readonly kind: 'empty' }
  | { readonly kind: 'bad_target' }
  | { readonly kind: 'too_broad'; readonly family: 4 | 6 }
  | { readonly kind: 'bad_prefix' }
  | { readonly kind: 'empty_reason' }
  | { readonly kind: 'reason_too_long' };

export interface ValidationOk {
  readonly ok: true;
  /** Normalised value to send on the wire. */
  readonly target: string;
  readonly kind: 'ip' | 'cidr';
}

export type Validation = ValidationOk | { readonly ok: false; readonly error: ValidationError };

/**
 * Founder bug 2026-10-20 — expand an IPv6 address to its /64 network
 * prefix. A home IPv6 privacy address is three or four nibbles that
 * rotate on a schedule; the /64 that contains it does not. Returns null
 * on obviously malformed input (the API's strict check still has final
 * say). Example:
 *   "2601:742:8101:aa10:1234:5678:9abc:def0" → "2601:742:8101:aa10::/64"
 *   "2601:742:8101:aa10::1"                  → "2601:742:8101:aa10::/64"
 */
export function ipv6NetworkPrefix(ip_raw: string): string | null {
  const ip = ip_raw.trim();
  if (ip.length === 0 || !ip.includes(':')) return null;
  // Expand `::` into the right number of ":0" groups so we can read the
  // first four hextets safely. Keep lower-cased for the compact form.
  const sides = ip.toLowerCase().split('::');
  if (sides.length > 2) return null;
  const left_groups = (sides[0] ?? '').split(':').filter((s) => s.length > 0);
  const right_groups = sides.length === 2 ? (sides[1] ?? '').split(':').filter((s) => s.length > 0) : [];
  const total = sides.length === 2 ? 8 - left_groups.length - right_groups.length : 0;
  if (total < 0) return null;
  const expanded = [
    ...left_groups,
    ...Array<string>(total).fill('0'),
    ...right_groups,
  ];
  if (expanded.length !== 8) return null;
  for (const g of expanded) if (!/^[0-9a-f]{1,4}$/.test(g)) return null;
  const prefix_groups = expanded.slice(0, 4);
  return `${prefix_groups.join(':')}::/64`;
}

/** Validate an "IP or range + reason" draft the same way the API does. */
export function validateTrustedDraft(target_raw: string, reason_raw: string): Validation {
  const target = target_raw.trim();
  const reason = reason_raw.trim();
  if (target === '') return { ok: false, error: { kind: 'empty' } };
  if (reason === '') return { ok: false, error: { kind: 'empty_reason' } };
  if (reason.length > 256) return { ok: false, error: { kind: 'reason_too_long' } };
  const slash = target.indexOf('/');
  if (slash === -1) {
    if (isIpv4(target)) return { ok: true, target, kind: 'ip' };
    if (isIpv6(target)) return { ok: true, target, kind: 'ip' };
    return { ok: false, error: { kind: 'bad_target' } };
  }
  const base = target.slice(0, slash);
  const bits_raw = target.slice(slash + 1);
  const bits = Number.parseInt(bits_raw, 10);
  if (!Number.isInteger(bits) || bits < 0 || String(bits) !== bits_raw) {
    return { ok: false, error: { kind: 'bad_prefix' } };
  }
  if (isIpv4(base)) {
    if (bits > 32) return { ok: false, error: { kind: 'bad_prefix' } };
    if (bits < 24) return { ok: false, error: { kind: 'too_broad', family: 4 } };
    return { ok: true, target, kind: 'cidr' };
  }
  if (isIpv6(base)) {
    if (bits > 128) return { ok: false, error: { kind: 'bad_prefix' } };
    if (bits < 48) return { ok: false, error: { kind: 'too_broad', family: 6 } };
    return { ok: true, target, kind: 'cidr' };
  }
  return { ok: false, error: { kind: 'bad_target' } };
}

/** Plain-words message for a validation error. */
function describeValidation(e: ValidationError): string {
  switch (e.kind) {
    case 'empty': return 'Enter an IP address or range.';
    case 'empty_reason': return 'Give a short reason, so your team knows why this address is trusted.';
    case 'reason_too_long': return 'Reasons can be up to 256 characters.';
    case 'bad_target': return 'That doesn’t look like an IP address or range.';
    case 'bad_prefix': return 'The /number after the slash should be a plain integer.';
    case 'too_broad':
      return e.family === 4
        ? 'A trusted IPv4 range can be no wider than /24.'
        : 'A trusted IPv6 range can be no wider than /48.';
    default: return 'Not accepted.';
  }
}

/** Map the API's error code into one plain sentence. */
export function describeApiError(err: string | undefined): string {
  switch (err) {
    case 'allowlist_cap_reached': return 'This site has reached the trusted-address limit (500). Remove one to add another.';
    case 'allowlist_too_broad': return 'That range is wider than /24 (IPv4) or /48 (IPv6). Narrow it.';
    case 'bad_target': return 'That isn’t a valid IP address or range.';
    case 'bad_reason': return 'The reason is empty or too long.';
    case 'forbidden': return 'Only owners and admins can trust addresses.';
    case 'unauthorized': return 'Your session expired. Sign in again.';
    case 'rate_limited': return 'Too many changes. Try again in a moment.';
    case 'not_found': return 'This site could not be found.';
    case 'bad_request': return 'The request was rejected.';
    default: return 'That didn’t work. Try again.';
  }
}

/* --------------------------- the view --------------------------- */

export interface TrustedViewProps {
  readonly site: Site;
}

export function TrustedView({ site }: TrustedViewProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.role : null;
  const canMutate = role === 'owner' || role === 'admin';
  const canReadMyIp = role === 'owner' || role === 'admin' || role === 'reviewer';
  const currentUserId = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.user_id : null;

  const [allowlist, setAllowlist] = useState<readonly AllowlistEntryWire[]>([]);
  // Founder 2026-10-20: hold the "No trusted addresses" copy until the
  // first fetch settles. Reset on every site change (per-site list).
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [myIpLoading, setMyIpLoading] = useState(false);
  const [myIpError, setMyIpError] = useState<string | null>(null);
  // Founder bug 2026-10-20 — home IPv6 privacy addresses rotate often,
  // so when "Trust my current address" resolves an IPv6 we also offer
  // the /64 so the merchant's own network can stay trusted. `null`
  // means we have not resolved an address yet.
  const [myIp, setMyIp] = useState<{ readonly ip: string; readonly family: 4 | 6 } | null>(null);

  const refetch = useCallback(async () => {
    if (http === null) return;
    try {
      const al = await listAllowlist(http, site.site_id);
      setAllowlist(al.entries);
    } catch { /* keep prior state on a flap */ } finally {
      setLoaded(true);
    }
  }, [http, site.site_id]);

  // Founder bug 2026-10-20 (Report 5) — "safe list shared between all
  // sites?" Trusted addresses are per-site in the API, but the view
  // kept the previously-loaded `allowlist` around during the site
  // switch, so for one tick the merchant saw the previous site's
  // rows. Clear the local state on every site change BEFORE refetch,
  // so a flap can't surface another site's data.
  useEffect(() => {
    setAllowlist([]);
    setLoaded(false);
    void refetch();
  }, [site.site_id, refetch]);
  useEffect(() => {
    const onFocus = (): void => { if (document.visibilityState === 'visible') void refetch(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetch]);

  const onRemove = useCallback(async (allow_id: string) => {
    if (http === null) return;
    setPending(`allow:${allow_id}`);
    setError(null);
    try {
      await deleteAllowlistEntry(http, site.site_id, allow_id);
      await refetch();
    } catch {
      setError('Could not remove the trusted entry. Try again.');
    } finally {
      setPending(null);
    }
  }, [http, site.site_id, refetch]);

  const onSubmit = useCallback(async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e !== undefined) e.preventDefault();
    if (http === null) return;
    setError(null);
    const v = validateTrustedDraft(target, reason);
    if (!v.ok) {
      setError(describeValidation(v.error));
      return;
    }
    setPending('add');
    try {
      await addAllowlistEntry(http, site.site_id, { target: v.target, reason: reason.trim() });
      setTarget('');
      setReason('');
      await refetch();
    } catch (err) {
      const api = err as ApiError | undefined;
      const code = api?.server_reason ?? api?.kind;
      setError(describeApiError(code ?? undefined));
    } finally {
      setPending(null);
    }
  }, [http, site.site_id, refetch, target, reason]);

  const onTrustMyIp = useCallback(async () => {
    if (http === null) return;
    setMyIpError(null);
    setMyIpLoading(true);
    try {
      const res = await getMyIp(http, site.site_id);
      if (res.ip === null) {
        setMyIp(null);
        setMyIpError('Could not read your address. Enter it by hand.');
      } else {
        const family = res.family ?? (res.ip.includes(':') ? 6 : 4);
        setMyIp({ ip: res.ip, family });
        setTarget(res.ip);
        if (reason.trim() === '') setReason('My current address');
      }
    } catch (err) {
      const api = err as ApiError | undefined;
      if (api?.kind === 'forbidden') {
        setMyIpError('You do not have permission to read your address.');
      } else {
        setMyIpError('Could not read your address. Try again.');
      }
    } finally {
      setMyIpLoading(false);
    }
  }, [http, site.site_id, reason]);

  /**
   * Founder bug 2026-10-20 — "Trust this network (/64)" button, offered
   * only when the resolver returned an IPv6. Produces the host prefix at
   * /64 ({@link ipv6NetworkPrefix}) and pre-fills a reason that names
   * the network.
   */
  const onTrustMyNetwork = useCallback(() => {
    if (myIp === null || myIp.family !== 6) return;
    const prefix = ipv6NetworkPrefix(myIp.ip);
    if (prefix === null) return;
    setTarget(prefix);
    if (reason.trim() === '' || reason.trim() === 'My current address') {
      setReason('My home network (IPv6 /64)');
    }
  }, [myIp, reason]);

  return (
    <Card data-testid="security-trusted">
      <div style={{ padding: '20px 24px 24px' }}>
        <h3 style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>
          Trusted addresses
        </h3>
        <div data-testid="trusted-intro" style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.55, marginBottom: 10 }}>
          Addresses here are never blocked or alerted on. Use it for your office, your team, your uptime monitor, your own servers.
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 16 }}>
          Trust your office or VPN so your team can always reach your admin pages, even if one of your addresses trips an alert. Attack paths still apply to everyone; your admin and login pages are never on that list.
        </div>

        {canMutate ? (
          <form
            data-testid="trusted-add-form"
            onSubmit={(e) => void onSubmit(e)}
            style={{
              padding: '14px 16px',
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 3,
              marginBottom: 20,
            }}
          >
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 10 }}>
              ADD A TRUSTED ADDRESS
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 220px' }}>
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                  IP address or range
                </span>
                <input
                  data-testid="trusted-target-input"
                  type="text"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="203.0.113.42 or 198.51.100.0/24"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={pending === 'add'}
                  style={{
                    fontFamily: monoFont, fontSize: 13, color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.border}`, borderRadius: 2,
                    padding: '7px 10px', minHeight: 36,
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 280px' }}>
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                  Reason
                </span>
                <input
                  data-testid="trusted-reason-input"
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="office VPN, uptime monitor, Stripe webhook"
                  maxLength={257}
                  disabled={pending === 'add'}
                  style={{
                    fontFamily: sansFont, fontSize: 13, color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.border}`, borderRadius: 2,
                    padding: '7px 10px', minHeight: 36,
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="submit"
                data-testid="trusted-add"
                disabled={pending === 'add'}
                style={{
                  background: C.lime, color: C.ink,
                  border: `1px solid ${C.lime}`, borderRadius: 2,
                  padding: '7px 14px', fontFamily: sansFont, fontSize: 13, fontWeight: 600,
                  cursor: pending === 'add' ? 'wait' : 'pointer',
                }}
              >
                {pending === 'add' ? 'Trusting…' : 'Trust this address'}
              </button>
              {canReadMyIp ? (
                <button
                  type="button"
                  data-testid="trusted-trust-my-ip"
                  onClick={() => void onTrustMyIp()}
                  disabled={myIpLoading}
                  style={{
                    background: 'transparent', color: C.text,
                    border: `1px solid ${C.border}`, borderRadius: 2,
                    padding: '7px 12px', fontFamily: sansFont, fontSize: 13,
                    cursor: myIpLoading ? 'wait' : 'pointer',
                  }}
                >
                  {myIpLoading ? 'Reading…' : 'Trust my current address'}
                </button>
              ) : null}
              {canReadMyIp && myIp !== null && myIp.family === 6 ? (
                <button
                  type="button"
                  data-testid="trusted-trust-my-network"
                  onClick={() => onTrustMyNetwork()}
                  disabled={myIpLoading}
                  style={{
                    background: 'transparent', color: C.text,
                    border: `1px solid ${C.border}`, borderRadius: 2,
                    padding: '7px 12px', fontFamily: sansFont, fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Trust this network (/64)
                </button>
              ) : null}
              {myIpError !== null ? (
                <span data-testid="trusted-my-ip-error" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
                  {myIpError}
                </span>
              ) : null}
            </div>
            {myIp !== null && myIp.family === 6 ? (
              <div
                data-testid="trusted-ipv6-note"
                style={{
                  marginTop: 8,
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                Home IPv6 addresses rotate often. Trusting your /64 network keeps every address your router hands out trusted.
              </div>
            ) : null}
            {error !== null ? (
              <div
                data-testid="trusted-form-error"
                role="alert"
                style={{
                  marginTop: 10,
                  padding: '7px 10px',
                  border: `1px solid ${C.coral}`,
                  borderRadius: 2,
                  fontFamily: sansFont, fontSize: 12, color: C.text,
                }}
              >
                {error}
              </div>
            ) : null}
          </form>
        ) : null}

        <TrustedAddressTable
          allowlist={allowlist}
          loading={!loaded}
          canMutate={canMutate}
          pendingId={pending?.startsWith('allow:') === true ? pending.slice('allow:'.length) : null}
          onRemove={(id) => void onRemove(id)}
          currentUserId={currentUserId}
        />
      </div>
    </Card>
  );
}
